import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import React, { useEffect, useMemo, useState } from 'react';

import {
  BulkHitSelection,
  BulkHitVisibilityOperation,
} from '../Tables/bulkHitSelection';

const MAX_REASON_LENGTH = 1000;

interface BulkDismissHitsModalProps {
  open: boolean;
  selections: BulkHitSelection[];
  isProcessing: boolean;
  onClose: () => void;
  onRemove: (key: string) => void;
  onConfirm: (comment: string) => Promise<void>;
  operation: BulkHitVisibilityOperation;
}

const BulkDismissHitsModal: React.FC<BulkDismissHitsModalProps> = ({
  open,
  selections,
  isProcessing,
  onClose,
  onRemove,
  onConfirm,
  operation,
}) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (open) {
      setComment('');
    }
  }, [open]);

  const groups = useMemo(() => {
    const grouped = new Map<
      string,
      { entityName: string; hits: BulkHitSelection[] }
    >();

    selections.forEach((selection) => {
      const existing = grouped.get(selection.databaseEntityId);
      if (existing) {
        existing.hits.push(selection);
        return;
      }
      grouped.set(selection.databaseEntityId, {
        entityName: selection.entityName,
        hits: [ selection ],
      });
    });

    return Array.from(grouped.entries());
  }, [selections]);

  const handleConfirm = async () => {
    if (selections.length === 0 || isProcessing) {
      return;
    }
    try {
      await onConfirm(comment.trim());
    } catch {
      // The parent surfaces the API error and keeps this review open for retry.
    }
  };
  const isUnhide = operation === 'unhide';
  const operationLabel = isUnhide ? 'unhide' : 'hide';
  const isDisabled = isProcessing || selections.length === 0;

  return (
    <Modal
      open={open}
      onClose={isProcessing ? undefined : onClose}
      aria-labelledby='bulk-dismiss-hits-title'
    >
      <div className='audit-add-modal-cont'>
        <div className='audit-add-modal-cont-head'>
          <div className='audit-add-modal-head-cont'>
            <div
              className={`audit-add-modal-head-cont-icons${
                isUnhide ? ' audit-add-modal-head-cont-icons--restore' : ''
              }`}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke={isUnhide ? 'var(--action-color)' : '#F04438'}
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                {isUnhide ? (
                  <>
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z' />
                    <circle cx='12' cy='12' r='3' />
                  </>
                ) : (
                  <>
                    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
                    <line x1='1' y1='1' x2='23' y2='23' />
                  </>
                )}
              </svg>
            </div>
            <div className='audit-add-modal-head-cont-supporting'>
              <h1 id='bulk-dismiss-hits-title'>
                {isUnhide ? 'Restore hits' : 'Hide hits'}
              </h1>
              <p>
                {isUnhide ? 'Show' : 'Hide'} {selections.length} hit
                {selections.length === 1 ? '' : 's'} across {groups.length}{' '}
                entit{groups.length === 1 ? 'y' : 'ies'}. You can optionally add
                a reason.
              </p>
            </div>
          </div>
          <div
            className='audit-add-modal-head-close'
            onClick={isProcessing ? undefined : onClose}
            role='button'
            tabIndex={isProcessing ? -1 : 0}
            aria-label={`Close bulk ${operationLabel} review`}
            aria-disabled={isProcessing}
            onKeyDown={(event) => {
              if (
                !isProcessing &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                onClose();
              }
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='#85888E'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>

        <div className='audit-add-modal-cont-body bulk-hit-review-modal__body'>
          <div className='audit-add-modal-cont-body-frame'>
            <div className='bulk-hit-review-list'>
              {groups.map(([ databaseEntityId, group ]) => (
                <section
                  className='bulk-hit-review-group'
                  key={databaseEntityId}
                >
                  <div className='bulk-hit-review-group__heading'>
                    <strong>{group.entityName}</strong>
                    <span>
                      {group.hits.length} hit{group.hits.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div>
                    {group.hits.map((hit) => (
                      <div className='bulk-hit-review-row' key={hit.key}>
                        <span title={hit.hitName}>{hit.hitName}</span>
                        <button
                          type='button'
                          onClick={() => onRemove(hit.key)}
                          disabled={isProcessing}
                          aria-label={`Remove ${hit.hitName} from selection`}
                        >
                          <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div
              className='audit-add-modal-cont-body-frame-form-row-input-label'
              style={{ width: '100%' }}
            >
              <label
                htmlFor='bulk-dismiss-comment'
                className='audit-add-modal-cont-body-frame-form-row-input-label-text'
              >
                Reason (optional)
              </label>
              <textarea
                id='bulk-dismiss-comment'
                className='audit-add-modal-cont-body-frame-form-input'
                value={comment}
                maxLength={MAX_REASON_LENGTH}
                onChange={(event) => setComment(event.target.value)}
                placeholder={
                  isUnhide
                    ? 'e.g. Restored after a second compliance review'
                    : 'e.g. False positive — not the same entity'
                }
                disabled={isProcessing}
                style={{
                  width: '100%',
                  height: '96px',
                  paddingTop: '10px',
                  alignItems: 'flex-start',
                  resize: 'vertical',
                }}
              />
              <div className='bulk-hit-review-comment-meta'>
                <span>This reason will appear on every affected entity.</span>
                <span>
                  {comment.length} of {MAX_REASON_LENGTH.toLocaleString()}{' '}
                  characters
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={isProcessing ? undefined : onClose}
              role='button'
              aria-disabled={isProcessing}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Cancel
              </div>
            </div>
            <div
              className={
                isUnhide
                  ? 'audit-modal-cont-footer-content-btn-upload'
                  : 'audit-modal-cont-footer-content-btn-delete'
              }
              onClick={isDisabled ? undefined : handleConfirm}
              role='button'
              aria-disabled={isDisabled}
            >
              <div
                className='audit-modal-cont-footer-content-btn-text'
                style={{ color: '#FFF' }}
              >
                {isProcessing ? (
                  <>
                    <CircularProgress
                      size={16}
                      color='inherit'
                      style={{ marginRight: '8px' }}
                    />
                    {isUnhide ? 'Restoring hits…' : 'Hiding hits…'}
                  </>
                ) : (
                  <>
                    {isUnhide ? 'Restore' : 'Hide'} {selections.length} hit
                    {selections.length === 1 ? '' : 's'}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkDismissHitsModal;
